// Simple chunked upload utility for Axios + Backend custom chunk endpoints
// Usage:
//   await uploadFileInChunks({ axios, file, apiBase, authHeaders, onProgress, chunkSize, sessionMeta, finalPath, sli_id, cancelToken, parallel })
// - axios: axios instance
// - file: File (from input)
// - apiBase: e.g. `${process.env.VUE_APP_APIURL}upload`
// - authHeaders: optional headers (e.g., Authorization)
// - onProgress: function(percentage){}
// - chunkSize: in bytes (default: 5 * 1024 * 1024)
// - sessionMeta: { filename, filetype } optional override
// - finalPath: required for complete call on server (absolute path expected by backend)

const ENV_PARALLEL = parseInt(process.env.VUE_APP_UPLOAD_PARALLEL || '', 10);
const ENV_CHUNK_SIZE = parseInt(process.env.VUE_APP_UPLOAD_CHUNK_SIZE || '', 10);

export async function uploadFileInChunks({ axios, file, apiBase, authHeaders = {}, onProgress, chunkSize, sessionMeta = {}, finalPath, sli_id, cancelToken, parallel }) {
  if (!file) throw new Error('File is required');
  if (!apiBase) throw new Error('apiBase is required');
  if (!finalPath && !sli_id) throw new Error('finalPath or sli_id is required');

  // Resolve runtime defaults from env if not provided
  const resolvedChunkSize = Number.isFinite(chunkSize) ? chunkSize : (Number.isFinite(ENV_CHUNK_SIZE) ? ENV_CHUNK_SIZE : 5 * 1024 * 1024);
  const resolvedParallel = Number.isFinite(parallel) ? parallel : (Number.isFinite(ENV_PARALLEL) ? ENV_PARALLEL : 3);

  const totalSize = file.size;
  const filename = sessionMeta.filename || file.name;
  const filetype = sessionMeta.filetype || file.type || 'application/octet-stream';

  // 1) Initiate
  const initRes = await axios.post(`${apiBase}/chunk/initiate`, {
    filename,
    filesize: totalSize,
    filetype,
    chunkSize: resolvedChunkSize
  }, { headers: { ...authHeaders }, cancelToken });
  const sessionId = initRes.data.sessionId;
  const serverChunkSize = initRes.data.chunkSize || resolvedChunkSize;

  // 2) Slice and upload chunks in parallel
  const totalChunks = Math.ceil(totalSize / serverChunkSize);
  const progressByIndex = new Array(totalChunks).fill(0); // bytes uploaded per chunk
  let nextIndex = 0;

  const computeAndEmitProgress = () => {
    if (!onProgress) return;
    const uploaded = progressByIndex.reduce((a, b) => a + b, 0);
    const pct = Math.floor((uploaded / totalSize) * 100);
    onProgress(Math.min(pct, 100));
  };

  const uploadOne = async (index) => {
    const start = index * serverChunkSize;
    const end = Math.min(start + serverChunkSize, totalSize);
    const blob = file.slice(start, end);
    const arrayBuffer = await blob.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);
    const chunkSizeActual = end - start;

    await axios.post(`${apiBase}/chunk`, buffer, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-Upload-Session-Id': sessionId,
        'X-Chunk-Index': index,
        'X-Total-Chunks': totalChunks,
        ...authHeaders
      },
      cancelToken,
      onUploadProgress: (e) => {
        // Update per-chunk progress and emit aggregated
        progressByIndex[index] = Math.min(e.loaded || 0, chunkSizeActual);
        computeAndEmitProgress();
      }
    });

    // Ensure full chunk accounted when done
    progressByIndex[index] = chunkSizeActual;
    computeAndEmitProgress();
  };

  const worker = async () => {
    while (true) {
      const current = nextIndex;
      if (current >= totalChunks) return;
      nextIndex++;
      await uploadOne(current);
    }
  };

  const workers = new Array(Math.max(1, resolvedParallel)).fill(0).map(() => worker());
  await Promise.all(workers);

  // 3) Complete (assemble on server)
  const completeBody = finalPath ? { sessionId, finalPath } : { sessionId, sli_id };
  const completeRes = await axios.post(`${apiBase}/chunk/complete`, completeBody, { headers: { ...authHeaders }, cancelToken });

  if (onProgress) onProgress(100);
  return completeRes.data;
}
